import { useEffect, useRef, useState } from "react"

type FetchParams = Record<string, unknown>

/**
 * useDedupedRefetch
 * -----------------
 * Ejecuta `refetch(params)` UNA sola vez por cada combinación *de valores* de
 * parámetros, evitando que un mismo endpoint se llame 2 veces con las mismas
 * variables al entrar a un reporte.
 *
 * ¿Por qué existe este hook?
 * En las secciones de reportes el patrón original era:
 *
 *     useEffect(() => {
 *         if (apiDateFilter) {
 *             refetch({ ...params, page: currentPage, filtro, orden })
 *         }
 *     }, [apiDateFilter, currentPage, formattedMeta])
 *
 * El problema: `apiDateFilter` proviene de un `useMemo` en <Reports/> y
 * `formattedMeta` de otro `useMemo`; ambos cambian de REFERENCIA en cada
 * re-render aunque su CONTENIDO sea idéntico (arrays/objetos nuevos). Como
 * `useEffect` compara sus dependencias por identidad (`Object.is`), el efecto
 * se volvía a disparar y el endpoint se llamaba 2 veces con exactamente las
 * mismas variables.
 *
 * La solución definitiva NO es perseguir cada referencia inestable, sino
 * depender del VALOR real de los parámetros:
 *   1. Serializamos `params` a un string (`key`) con JSON.stringify.
 *   2. Guardamos el último `key` disparado en un `useRef` (sobrevive a los
 *      re-renders y NO provoca renders adicionales).
 *   3. Solo llamamos a `refetch` cuando ese `key` cambia realmente.
 *
 * Con esto:
 *   - Al entrar a un reporte se hace 1 sola llamada.
 *   - Se vuelve a llamar solo cuando cambia algo real (página, filtro, orden o
 *     rango de fechas).
 *   - Se neutraliza también el doble montaje de React.StrictMode en desarrollo,
 *     porque el ref persiste entre las dos invocaciones del efecto.
 *
 * @param refetch  Función que dispara la petición (p. ej. la `refetch` de useFetch).
 * @param params   Objeto de parámetros, o un valor "falsy" (null/undefined/false)
 *                 para NO ejecutar la petición todavía (p. ej. cuando aún no hay
 *                 rango de fechas disponible).
 * @returns `error` indica si la última petición falló y `retry` la vuelve a
 *          ejecutar con los últimos parámetros (para el empty state de error).
 */
export const useDedupedRefetch = (
    refetch: (params: FetchParams, onSuccess?: () => void, onError?: () => void) => void,
    params: FetchParams | null | undefined | false
) => {
    // Clave por VALOR: si el contenido de params no cambió, `key` tampoco.
    const key = params ? JSON.stringify(params) : null
    const lastFetchKey = useRef<string | null>(null)
    const lastParams = useRef<FetchParams | null>(null)
    // Id de la última petición: evita que la respuesta de una petición vieja
    // sobrescriba el estado de error de una más reciente.
    const requestId = useRef(0)
    const [error, setError] = useState(false)

    const run = (fetchParams: FetchParams) => {
        const id = ++requestId.current
        lastParams.current = fetchParams
        setError(false)
        refetch(
            fetchParams,
            () => id === requestId.current && setError(false),
            () => id === requestId.current && setError(true)
        )
    }

    const retry = () => {
        if (lastParams.current) {
            run(lastParams.current)
        }
    }

    useEffect(() => {
        // Sin params válidos (p. ej. aún no hay rango de fechas) no hacemos nada.
        if (!params || key === null) {
            return
        }
        // Mismo valor que la última llamada => evitamos el fetch duplicado.
        if (lastFetchKey.current === key) {
            return
        }
        lastFetchKey.current = key
        run(params)
        // Dependemos SOLO de `key` (el valor serializado). `refetch` y `params`
        // se omiten a propósito: useFetch recrea `refetch` en cada render y
        // `params` es un objeto nuevo por render; incluirlos reintroduciría el
        // problema de referencias inestables que este hook resuelve.
    }, [key])

    return { error, retry }
}

export default useDedupedRefetch
